/**
 * 画布素材 Store。
 *
 * 负责把 Yjs 顶层 `yAssetsData` 同步到 Valtio `canvasAssetsStore`，
 * 并提供素材写入、节点引用绑定/解绑等操作。这里不负责服务端素材库 API，
 * 也不主动删除画布素材条目，孤立素材交给后续服务端清理。
 */
import * as Y from 'yjs';
import { proxy } from 'valtio';
import { proxyMap } from 'valtio/utils';
import { yAssetsData, yCustomNodeDatas, ydoc } from '@/shared/config/yjs-config';
import type { CanvasAsset } from './canvas-asset-types';
import {
  appendNodeAssetRef,
  prependNodeAssetRef,
  readNodeAssetRefs,
  removeNodeAssetRef,
  writeNodeAssetRefs,
} from '../lib/node-asset-refs';

type YAssetMap = Y.Map<unknown>;

interface CanvasAssetsState {
  assets: Map<string, CanvasAsset>;
}

export const canvasAssetsStore = proxy<CanvasAssetsState>({
  assets: proxyMap<string, CanvasAsset>(),
});

/**
 * 将单个素材 Y.Map 转成普通 CanvasAsset。
 *
 * @param yAsset 存储单个素材字段的 Y.Map
 * @returns 可放入 Valtio store 的 CanvasAsset 快照
 */
function readYAsset(assetId: string, yAsset: YAssetMap): CanvasAsset {
  return {
    id: assetId,
    ...(yAsset.toJSON() as Omit<CanvasAsset, 'id'>),
  };
}

/**
 * 将 CanvasAsset 转成 Y.Map。
 *
 * @param asset 要写入 Yjs 的画布素材
 * @returns 字段级存储的 Y.Map，跳过 undefined 字段
 */
function assetToYMap(asset: CanvasAsset): YAssetMap {
  const yAsset = new Y.Map<unknown>();
  Object.entries(asset).forEach(([key, value]) => {
    if (key !== 'id' && value !== undefined) {
      yAsset.set(key, value);
    }
  });
  return yAsset;
}

/**
 * 增量更新单个素材 Y.Map 的字段。
 *
 * @param yAsset 已存在的素材 Y.Map
 * @param updates 要更新的字段；值为 undefined 时删除对应字段
 */
function writeAssetFields(yAsset: YAssetMap, updates: Partial<CanvasAsset>): void {
  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined) {
      yAsset.delete(key);
      return;
    }

    yAsset.set(key, value);
  });
}

/**
 * 按 assetId 获取素材 Y.Map。
 *
 * @param assetId 画布素材 ID
 * @returns 存在且类型正确时返回 Y.Map，否则返回 undefined
 */
function getCanvasAssetYMap(assetId: string): YAssetMap | undefined {
  const yAsset = yAssetsData.get(assetId);
  return yAsset instanceof Y.Map ? yAsset : undefined;
}

/**
 * 按 nodeId 获取节点 custom data。
 *
 * @param nodeId 节点 ID
 * @returns 存在且类型正确时返回节点 Y.Map，否则返回 undefined
 */
function getNodeDataYMap(nodeId: string): Y.Map<unknown> | undefined {
  const yNodeData = yCustomNodeDatas.get(nodeId);
  return yNodeData instanceof Y.Map ? yNodeData : undefined;
}

/**
 * 启动时从 yAssetsData 全量加载一次素材。
 *
 * 只在初始化时全量读取；后续协作变更必须走 observeDeep delta 增量同步。
 */
function loadAllAssets(): void {
  canvasAssetsStore.assets.clear();
  yAssetsData.forEach((yAsset, assetId) => {
    if (yAsset instanceof Y.Map) {
      canvasAssetsStore.assets.set(assetId, readYAsset(assetId, yAsset));
    }
  });
}

/**
 * 将 yAssetsData 的 observeDeep 事件应用到 Valtio store。
 *
 * @param events Yjs 深度监听事件集合
 *
 * 顶层事件处理素材新增/删除；嵌套事件只刷新对应 assetId，避免任何素材字段变化
 * 都重建整个 Map，降低大画布协作时的重渲染范围。
 */
function applyAssetDelta(events: Y.YEvent<Y.AbstractType<unknown>>[]): void {
  const nestedChangedAssetIds = new Set<string>();

  events.forEach((event) => {
    if (event.target === yAssetsData && event instanceof Y.YMapEvent) {
      event.changes.keys.forEach((change, assetId) => {
        if (change.action === 'delete') {
          canvasAssetsStore.assets.delete(assetId);
          return;
        }

        const yAsset = getCanvasAssetYMap(assetId);
        if (yAsset) {
          canvasAssetsStore.assets.set(assetId, readYAsset(assetId, yAsset));
        }
      });
      return;
    }

    const assetId = event.path[0];
    if (typeof assetId === 'string') {
      nestedChangedAssetIds.add(assetId);


    }
  });

  nestedChangedAssetIds.forEach((assetId) => {
    const yAsset = getCanvasAssetYMap(assetId);
    if (yAsset) {
      canvasAssetsStore.assets.set(assetId, readYAsset(assetId, yAsset));
    }
  });
}

/**
 * 初始化画布素材同步。
 *
 * @returns 取消监听函数；调用方应在画布卸载时执行，避免重复注册 observeDeep
 */
export function initCanvasAssetsStoreSync(): () => void {
  loadAllAssets();

  const observer = (events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
    applyAssetDelta(events);
  };
  yAssetsData.observeDeep(observer);

  return () => {
    yAssetsData.unobserveDeep(observer);
  };
}

function mergeCanvasAssetForWrite(asset: CanvasAsset): CanvasAsset {
  const existing = getCanvasAssetYMap(asset.id);
  if (!existing) {
    return asset;
  }

  const merged: CanvasAsset = { ...readYAsset(asset.id, existing) };
  Object.entries(asset).forEach(([key, value]) => {
    if (value !== undefined) {
      (merged as unknown as Record<string, unknown>)[key] = value;
    }
  });

  return merged;
}

/**
 * 新增或更新一个画布素材，并可选绑定到节点；素材已存在时合并有效字段，undefined 不覆盖已有数据。
 *
 * @param asset 要写入 `yAssetsData` 的画布素材
 * @param nodeId 可选节点 ID；传入时会把该节点 assetRefs 覆盖为当前素材
 */
export function addCanvasAsset(asset: CanvasAsset, nodeId?: string): void {
  ydoc.transact(() => {
    const nextAsset = mergeCanvasAssetForWrite(asset);
    yAssetsData.set(nextAsset.id, assetToYMap(nextAsset));

    if (nodeId) {
      setNodeAssetInsideTransaction(nodeId, nextAsset.id);
    }
  });
}

/**
 * 更新已有画布素材的部分字段。
 *
 * @param assetId 要更新的素材 ID
 * @param updates 字段更新；undefined 表示删除字段
 */
export function updateCanvasAsset(assetId: string, updates: Partial<CanvasAsset>): void {
  const yAsset = getCanvasAssetYMap(assetId);
  if (!yAsset) {
    return;
  }

  ydoc.transact(() => {
    writeAssetFields(yAsset, updates);
  });
}

/**
 * 在外层事务中覆盖节点的素材引用。
 *
 * @param nodeId 节点 ID
 * @param assetId 要设为唯一引用的素材 ID
 */
function setNodeAssetInsideTransaction(nodeId: string, assetId: string): void {
  const yNodeData = getNodeDataYMap(nodeId);
  if (!yNodeData) {
    return;
  }

  writeNodeAssetRefs(yNodeData, [assetId]);
}

/**
 * 覆盖节点的素材引用。
 *
 * @param nodeId 节点 ID
 * @param assetId 要设为唯一引用的素材 ID，适合图片/视频/音频单素材节点
 */
export function setNodeAsset(nodeId: string, assetId: string): void {
  ydoc.transact(() => {
    setNodeAssetInsideTransaction(nodeId, assetId);
  });
}

/**
 * 向节点追加素材引用。
 *
 * @param nodeId 节点 ID
 * @param assetId 要追加的素材 ID；重复 ID 会被 helper 去重
 */
export function appendNodeAsset(nodeId: string, assetId: string): void {
  const yNodeData = getNodeDataYMap(nodeId);
  if (!yNodeData) {
    return;
  }

  ydoc.transact(() => {
    appendNodeAssetRef(yNodeData, assetId);
  });
}

/**
 * 在节点引用开头插入素材引用。
 *
 * @param nodeId 节点 ID
 * @param assetId 要插入到开头的素材 ID；重复 ID 会被 helper 去重
 */
export function prependNodeAsset(nodeId: string, assetId: string): void {
  const yNodeData = getNodeDataYMap(nodeId);
  if (!yNodeData) {
    return;
  }

  ydoc.transact(() => {
    prependNodeAssetRef(yNodeData, assetId);
  });
}

/**
 * 解除节点对某个素材的引用。
 *
 * @param nodeId 节点 ID
 * @param assetId 要解除引用的素材 ID
 */
export function unlinkNodeAsset(nodeId: string, assetId: string): void {
  const yNodeData = getNodeDataYMap(nodeId);
  if (!yNodeData) {
    return;
  }

  ydoc.transact(() => {
    removeNodeAssetRef(yNodeData, assetId);
  });
}

/**
 * 从所有节点中移除某个素材引用。
 *
 * @param assetId 要从节点引用中解除的画布素材 ID
 *
 * 这里只解除引用，不删除 `yAssetsData[assetId]`，避免协作场景下一个客户端误删
 * 另一个客户端仍需要的素材数据。
 */
export function detachCanvasAsset(assetId: string): void {
  ydoc.transact(() => {
    yCustomNodeDatas.forEach((yNodeData) => {
      if (!(yNodeData instanceof Y.Map)) {
        return;
      }

      const assetRefs = readNodeAssetRefs(yNodeData);
      if (assetRefs.includes(assetId)) {
        removeNodeAssetRef(yNodeData, assetId);
      }
    });
  });
}
